import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { CrudPage } from "@/components/crud/CrudPage";
import { UserForm } from "./UserForm";
import {
	createUser,
	createUserCompany,
	createUserCompanyModule,
	deleteUser,
	deleteUserCompany,
	deleteUserCompanyModule,
	getModulesByUserCompany,
	getUser,
	getUserCompanies,
	getUsers,
	updateUser,
	UserRoleLabels,
	UserRoles,
	type User,
} from "@/api/user.service";
import { grantModuleRole, revokeModuleRole } from "@/api/moduleRole.service";
import {
	Autocomplete,
	Button,
	Checkbox,
	IconButton,
	Stack,
	TextField,
} from "@mui/material";
import {
	Sync,
	Add,
	Edit,
	Delete,
	Block,
	CheckCircle,
} from "@mui/icons-material";
import { FilterModal } from "@/components/FilterModal";
import usePagination from "@/hooks/pagination";
import { formatPhoneBR } from "@/utils/phoneUtils";
import { ConfirmModal } from "@/components/ConfirmModal";
import { useNotification } from "@/context/NotificationContext";
import { useCrud } from "@/hooks/useCrud";
import { DataTable } from "@/components/DataTable";
import { CrudModal } from "@/components/crud/CrudModal";
import { useLogger } from "@/hooks/useLogger";
import { LogEventType } from "@/api/log.service";
import { useAuth } from "@/context/AuthContext";

type UserFilter = {
	name?: string;
	email?: string;
	role?: string;
	phone?: string;
	active?: boolean;
};

export function UsersPage() {
	const { total, setTotal, page, setPage, rowsPerPage, setRowsPerPage } =
		usePagination();
	const { notify } = useNotification();
	const { user } = useAuth();
	const { log } = useLogger();
	const location = useLocation();
	const { open, editing, handleCreate, handleEdit, handleClose } =
		useCrud<User>();

	// Filters
	const [inputValues, setInputValues] = useState({
		name: "",
		email: "",
		role: "",
		phone: "",
	});
	const [filters, setFilters] = useState<UserFilter>({});

	// Data Fetching
	const [users, setUsers] = useState<User[]>([]);
	const [isLoading, setIsLoading] = useState(true);

	// Delete Modal State
	const [deleteId, setDeleteId] = useState<string | null>(null);
	const userToDelete = users.find((u) => u.id === deleteId);

	// Toggle Active Modal State
	const [toggleActiveUser, setToggleActiveUser] = useState<User | null>(null);

	const roleOptions = Object.values(UserRoles)
		.filter((role) => {
			if (!user) return role != UserRoles.Admin;

			return user.role == UserRoles.Admin || role != UserRoles.Admin;
		})
		.map((role) => ({
			id: role,
			label: UserRoleLabels[role],
		}));

	const loadData = async () => {
		// setIsLoading(true);
		try {
			const userData = await getUsers({
				limit: rowsPerPage,
				offset: page * rowsPerPage,
				...filters,
			});
			setUsers(userData.users ?? []);
			setTotal(userData.total ?? 0);
		} catch (error) {
			notify("Erro ao carregar dados", "error");
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		loadData();
	}, [page, rowsPerPage, filters]);

	useEffect(() => {
		const editProfileId = location.state?.editProfileId;
		if (!editProfileId || isLoading) return;

		getUser(editProfileId).then((profileUser) => {
			handleEdit(profileUser);
			window.history.replaceState({}, "");
		});
	}, [location.state?.editProfileId, isLoading]);

	const onSave = async (values: any) => {
		try {
			const {
				companies,
				companyModules,
				moduleRoles,
				moduleRolesInitial,
				moduleRolesClientId,
				...userFields
			} = values;

			if (!userFields.name) {
				notify("Nome é obrigatório", "error");
				return;
			}

			if (!userFields.email) {
				notify("Email é obrigatório", "error");
				return;
			}

			if (!userFields.role) {
				notify("Cargo é obrigatório", "error");
				return;
			}

			// Só admin decide quem é conta de serviço; os demais nem mandam o campo.
			if (user?.role !== UserRoles.Admin)
				delete userFields.is_service_account;
			const isServiceAccount = !!userFields.is_service_account;
			if (isServiceAccount) delete userFields.password;

			if (!userFields.phone && !isServiceAccount) {
				notify("Telefone é obrigatório", "error");
				return;
			}

			if (!userFields.password && !editing && !isServiceAccount) {
				notify("Senha é obrigatório no cadastro", "error");
				return;
			}

			if (!companies || companies.length === 0) {
				notify("Selecione pelo menos uma empresa", "error");
				return;
			}

			let userId: string;

			if (editing) {
				if (!userFields.password) delete userFields.password;
				const result = await updateUser(editing.id, userFields);
				userId = editing.id;
				log(LogEventType.Updated, result, "user", result.id, "info");
			} else {
				const result = await createUser(userFields as any);
				userId = result.id;
				log(LogEventType.Created, result, "user", result.id, "info");
			}

			// Handle company associations
			if (companies) {
				// Usuário recém-criado ainda não tem vínculo nenhum, e buscá-los é
				// negado para o requester "cliente" (ele só enxerga usuários com quem
				// compartilha empresa) — o GET só faz sentido na edição.
				const currentCompanies = editing
					? await getUserCompanies(userId)
					: [];
				const currentIds = (currentCompanies ?? []).map((c) => c.id);
				const toAdd = (companies as string[]).filter(
					(id) => !currentIds.includes(id),
				);
				const toRemove = currentIds.filter(
					(id) => !(companies as string[]).includes(id),
				);
				await Promise.all([
					...toAdd.map((id) =>
						createUserCompany({ user_id: userId, company_id: id }),
					),
					...toRemove.map((id) =>
						deleteUserCompany({ user_id: userId, company_id: id }),
					),
				]);
			}

			// Handle module assignments
			if (companyModules) {
				await Promise.all(
					Object.entries(
						companyModules as Record<string, string[]>,
					).map(async ([companyId, desiredIds]) => {
						const current = await getModulesByUserCompany({
							user_id: userId,
							company_id: companyId,
						});
						const currentIds = (current ?? []).map(
							(m) => m.module_id,
						);
						const toAdd = desiredIds.filter(
							(id) => !currentIds.includes(id),
						);
						const toRemove = currentIds.filter(
							(id) => !desiredIds.includes(id),
						);
						await Promise.all([
							...toAdd.map((id) =>
								createUserCompanyModule({
									user_id: userId,
									company_id: companyId,
									module_id: id,
								}),
							),
							...toRemove.map((id) =>
								deleteUserCompanyModule({
									user_id: userId,
									company_id: companyId,
									module_id: id,
								}),
							),
						]);
					}),
				);
			}

			// Cargos de módulo (MODULE_ROLES): só grava por role que realmente
			// mudou em relação ao que foi carregado, pra não gerar evento de
			// auditoria à toa a cada save.
			if (moduleRolesClientId && moduleRoles) {
				const calls: Promise<unknown>[] = [];
				Object.entries(moduleRoles as Record<string, string[]>).forEach(
					([moduleId, desiredRoles]) => {
						const initialRoles: string[] =
							moduleRolesInitial?.[moduleId] ?? [];
						const desired = new Set(desiredRoles);
						const initial = new Set(initialRoles);
						new Set([...desired, ...initial]).forEach((role) => {
							if (desired.has(role) === initial.has(role)) return;
							const action = desired.has(role)
								? grantModuleRole
								: revokeModuleRole;
							calls.push(
								action({
									user_id: userId,
									client_id: moduleRolesClientId,
									module_id: moduleId,
									role,
								}),
							);
						});
					},
				);
				await Promise.all(calls);
			}

			notify(`Usuário ${editing ? "atualizado" : "criado"}!`, "success");
			loadData();
			handleClose();
		} catch (e: any) {
			console.error("Save error:", e);
			const errorMsg = e?.message || "Erro ao salvar";
			notify(errorMsg, "error");
		}
	};

	const onDeleteConfirm = async () => {
		if (!deleteId) return;
		try {
			await deleteUser(deleteId);
			log(
				LogEventType.Deleted,
				{ id: deleteId },
				"user",
				deleteId,
				"info",
			);

			notify("Deletado!", "success");
			loadData();
		} finally {
			setDeleteId(null);
		}
	};

	const onToggleActiveConfirm = async () => {
		if (!toggleActiveUser) return;
		try {
			const active = !toggleActiveUser.active;
			const result = await updateUser(toggleActiveUser.id, { active });
			log(
				active ? LogEventType.Activated : LogEventType.Deactivated,
				result,
				"user",
				toggleActiveUser.id,
				"info",
			);

			notify(
				active ? "Usuário ativado!" : "Usuário inativado!",
				"success",
			);
			loadData();
		} catch (e: any) {
			notify(e?.message || "Erro ao atualizar status", "error");
		} finally {
			setToggleActiveUser(null);
		}
	};

	const handleInputChange = (
		field: keyof typeof inputValues,
		value: string,
	) => {
		setInputValues((prev) => ({ ...prev, [field]: value }));
	};

	const applyFilters = () => {
		setFilters({
			name: inputValues.name || undefined,
			email: inputValues.email || undefined,
			phone: inputValues.phone || undefined,
			role: inputValues.role || undefined,
		});
		setPage(0);
	};

	const handleClearFilters = () => {
		const empty = { name: "", email: "", role: "", phone: "" };
		setInputValues(empty);
		setFilters({});
		setPage(0);
	};

	const activeFilterCount = Object.values(filters).filter(Boolean).length;

	if (isLoading) return <Sync />;

	return (
		<CrudPage
			title="Usuários"
			subtitle="Gerencie os usuários do sistema"
			headerActions={
				<Button
					variant="contained"
					startIcon={<Add />}
					onClick={handleCreate}
				>
					Novo Usuário
				</Button>
			}
		>
			<FilterModal
				activeCount={activeFilterCount}
				onApply={applyFilters}
				onClear={handleClearFilters}
			>
				<TextField
					label="Nome"
					size="small"
					fullWidth
					value={inputValues.name}
					onChange={(e) => handleInputChange("name", e.target.value)}
				/>
				<TextField
					label="Email"
					size="small"
					fullWidth
					value={inputValues.email}
					onChange={(e) => handleInputChange("email", e.target.value)}
					autoComplete="new-email"
				/>
				<TextField
					label="Telefone"
					size="small"
					fullWidth
					value={inputValues.phone}
					onChange={(e) => handleInputChange("phone", e.target.value)}
				/>
				<Autocomplete
					options={roleOptions}
					size="small"
					fullWidth
					value={
						roleOptions.find((s) => s.id === inputValues.role) ||
						null
					}
					onChange={(_, newValue) => {
						handleInputChange("role", newValue ? newValue.id : "");
					}}
					getOptionLabel={(option) => option.label}
					isOptionEqualToValue={(option, value) =>
						option.id === value.id
					}
					renderInput={(params) => (
						<TextField
							{...params}
							label="Cargo"
							variant="outlined"
						/>
					)}
				/>
			</FilterModal>
			<DataTable
				data={users}
				columns={[
					{
						key: "active",
						label: "Ativo",
						render: (u) => <Checkbox checked={u.active} disabled />,
					},
					{ key: "name", label: "Nome" },
					{ key: "email", label: "Email" },
					{
						key: "role",
						label: "Cargo",
						render: (u) =>
							u.is_service_account
								? `${UserRoleLabels[u.role]} (conta de serviço)`
								: UserRoleLabels[u.role],
					},
					{
						key: "phone",
						label: "Telefone",
						render: (u) => formatPhoneBR(u.phone),
					},
				]}
				total={total}
				page={page}
				rowsPerPage={rowsPerPage}
				onPageChange={setPage}
				onRowsPerPageChange={setRowsPerPage}
				actions={(rowUser) => (
					<Stack
						direction="row"
						spacing={1}
						justifyContent="flex-end"
					>
						<IconButton
							aria-label={`Editar ${rowUser.name}`}
							onClick={() => handleEdit(rowUser)}
						>
							<Edit />
						</IconButton>
						<IconButton
							aria-label={`${rowUser.active ? "Inativar" : "Ativar"} ${rowUser.name}`}
							onClick={() => setToggleActiveUser(rowUser)}
							color={rowUser.active ? "warning" : "success"}
						>
							{rowUser.active ? <Block /> : <CheckCircle />}
						</IconButton>
						{user?.role === UserRoles.Admin && (
							<IconButton
								aria-label={`Excluir ${rowUser.name}`}
								onClick={() => setDeleteId(rowUser.id)}
								color="error"
							>
								<Delete />
							</IconButton>
						)}
					</Stack>
				)}
			/>

			<CrudModal
				open={open}
				onClose={handleClose}
				title={editing ? "Editar Usuário" : "Novo Usuário"}
				initialData={editing}
				onSubmit={onSave}
				Form={UserForm}
				extraFormProps={{
					savedServiceAccount: !!editing?.is_service_account,
				}}
			/>

			<ConfirmModal
				open={!!deleteId}
				onClose={() => setDeleteId(null)}
				onConfirm={onDeleteConfirm}
				title="Exclusão"
				btnText="Confirmar"
				text={`Deseja excluir ${userToDelete?.name}?`}
			/>

			<ConfirmModal
				open={!!toggleActiveUser}
				onClose={() => setToggleActiveUser(null)}
				onConfirm={onToggleActiveConfirm}
				title={toggleActiveUser?.active ? "Inativação" : "Ativação"}
				btnText="Confirmar"
				text={`Deseja ${toggleActiveUser?.active ? "inativar" : "ativar"} ${toggleActiveUser?.name}?`}
			/>
		</CrudPage>
	);
}
